import React, { useState } from 'react';
import {
  AlertBanner,
  Button,
  ExpandableText,
  Modal,
  SparkIcon,
  SparkModalPortal,
  Spinner,
  Text,
  sparkToast,
} from '@spark';
import { handleCopy, formatSize } from '@/shared/utils/helpers';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import {
  CURRENT_VERSION_EVENT_ID,
  downloadCurrentTrainSet,
  downloadTrainSetVersion,
} from '../../api/versionsDiffApi';
import { useVersionsDiff } from './useVersionsDiff';
import LabelDistributionSection from './LabelDistributionSection';
import ChangedRecordsSection from './ChangedRecordsSection';
import RecordDiffModal from './RecordDiffModal';
import './versionsDiff.css';

const asDisplayString = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/** One params-table cell: clamped text with a copy icon button in the corner. */
const ParamCell = ({ value }) => {
  const text = asDisplayString(value);
  if (!text) {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }
  return (
    <div className="vdm-param-cell">
      <ExpandableText text={text} maxLines={3} className="vdm-param-text" />
      {/* Icon-only Spark Buttons render their own tooltip (from aria-label);
          wrapping them in another TooltipTrigger would stack two tooltips. */}
      <Button
        variant="ghost"
        size="tiny"
        type="button"
        aria-label="Copy"
        icon={<SparkIcon slug="copy" size="small" />}
        onClick={() => handleCopy(text)}
      />
    </div>
  );
};

/** "train_set_V{n}.json" download card for one version's train set snapshot. */
const TrainSetCard = ({ summary, eventId, clientId, usecaseId, taskType, versionLabel, taskName }) => {
  const [downloading, setDownloading] = useState(false);
  const sizeBytes = summary?.size_bytes || 0;
  // Version labels already carry the "V" prefix (e.g. "V4.03").
  const displayName = `train_set_${versionLabel || 'version'}.json`;
  const downloadName = taskName ? `${taskName}_${displayName}` : displayName;
  const handleDownload = async () => {
    setDownloading(true);
    try {
      if (eventId === CURRENT_VERSION_EVENT_ID) {
        // The current side has no snapshot — download the live train datasets.
        await downloadCurrentTrainSet({
          client_id: clientId,
          llm_usecase_id: usecaseId,
          task_type: taskType,
          fileName: downloadName,
        });
      } else {
        await downloadTrainSetVersion({
          client_id: clientId,
          distillation_event_id: eventId,
          fileName: downloadName,
        });
      }
    } catch (error) {
      sparkToast.error(error?.message || 'Failed to download train set');
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="vdm-trainset-card">
      <SparkIcon slug="description" size="large" className="vdm-trainset-icon" />
      <div className="vdm-trainset-copy">
        <Text variant="base-semibold" color="primary">{displayName}</Text>
        <Text variant="detail-regular" color="secondary">
          {sizeBytes ? formatSize(sizeBytes) : '-'}
        </Text>
      </div>
      <Button
        variant="secondary"
        size="tiny"
        type="button"
        aria-label="Download train set"
        disabled={downloading}
        icon={
          downloading ? <Spinner size="small" /> : <SparkIcon slug="download" size="small" />
        }
        onClick={handleDownload}
      />
    </div>
  );
};

/**
 * "Versions Diff" modal (Figma node 5196:12142): params diff table, label
 * distribution + changed records collapsible sections, and per-version
 * train-set download cards. Compares two SpecificAI model versions, or a
 * version against the CURRENT datasets when `toEventId` is
 * `CURRENT_VERSION_EVENT_ID` (params then come from `currentUsecase`).
 */
const VersionsDiffModal = ({
  clientId,
  usecaseId,
  fromEventId,
  toEventId,
  fromVersionLabel,
  toVersionLabel,
  fromBenchmarkLabel,
  toBenchmarkLabel,
  fromModelLabel,
  toModelLabel,
  currentUsecase = null,
  taskName,
  taskType = null,
  singleLabel = false,
  allLabels = [],
  onClose,
}) => {
  const {
    details,
    detailsLoading,
    diff,
    diffError,
    diffLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
  } = useVersionsDiff({ clientId, usecaseId, fromEventId, toEventId });
  const [viewedRecord, setViewedRecord] = useState(null);

  // Generative task types diff the response text (their summary/output)
  // instead of labels/entities, under a task-type-specific column name.
  const outputLabel = isGenerativeTask(taskType)
    ? (taskType === TaskType.contentGeneration ? 'Output' : 'Summary')
    : null;

  const fromEvent = details.from?.distillation_event;
  // The current side has no distillation event — the usecase carries the live
  // params (`prompt` is the field the event stamps as `instruction`).
  const toEvent =
    toEventId === CURRENT_VERSION_EVENT_ID
      ? currentUsecase && {
          teacher_system_prompt: currentUsecase.teacher_system_prompt,
          instruction: currentUsecase.prompt,
          teacher_schema: currentUsecase.teacher_schema,
          benchmarks: currentUsecase.benchmarks,
        }
      : details.to?.distillation_event;

  // Explicit labels (Evaluation's runs table) win; fall back to the event's /
  // usecase's benchmark names when opened from the task page.
  const benchmarkFallback = (event) => (event?.benchmarks || []).join(', ');

  const paramRows = [
    {
      key: 'benchmark',
      label: 'Benchmark Used',
      render: (side) => (
        <div className="vdm-benchmark-cell">
          <Text variant="base-regular" color="primary">
            {(side === 'from'
              ? fromBenchmarkLabel || benchmarkFallback(fromEvent)
              : toBenchmarkLabel || benchmarkFallback(toEvent)) || '-'}
          </Text>
          <Text variant="detail-regular" color="secondary">
            {(side === 'from' ? fromModelLabel : toModelLabel) || ''}
          </Text>
        </div>
      ),
    },
    {
      key: 'system-prompt',
      label: 'System Prompt Used',
      render: (side) => (
        <ParamCell value={(side === 'from' ? fromEvent : toEvent)?.teacher_system_prompt} />
      ),
    },
    {
      key: 'prompt',
      label: 'Prompt Used',
      render: (side) => (
        <ParamCell value={(side === 'from' ? fromEvent : toEvent)?.instruction} />
      ),
    },
    {
      key: 'json-schema',
      label: 'JSON Schema',
      render: (side) => (
        <ParamCell value={(side === 'from' ? fromEvent : toEvent)?.teacher_schema} />
      ),
    },
  ];

  return (
    <>
      {/* verticalAlign="top" keeps a constant gap below the sticky navbar;
          the default centered mode lets this tall modal rise underneath it. */}
      <SparkModalPortal open onClose={onClose} width={950} verticalAlign="top" destroyOnClose>
        <Modal
          title="Versions Diff"
          subtitle="See how the training datasets differ between the selected versions"
          showMainButton={false}
          tertiaryLabel="Close"
          className="vdm-modal"
          onTertiaryClick={onClose}
          onCloseClick={onClose}
        >
          <div className="vdm-body">
            <div className="vdm-table vdm-params-table" role="table">
              <div className="vdm-table-header-row" role="row">
                <Text variant="uniq-semibold-table-caps" color="secondary" role="columnheader">
                  Parameters
                </Text>
                <Text variant="uniq-semibold-table-caps" color="secondary" role="columnheader">
                  {fromVersionLabel}
                </Text>
                <Text variant="uniq-semibold-table-caps" color="secondary" role="columnheader">
                  {toVersionLabel}
                </Text>
              </div>
              {detailsLoading ? (
                <div className="vdm-loading"><Spinner size="small" /></div>
              ) : (
                paramRows.map((row) => (
                  <div className="vdm-table-row" role="row" key={row.key}>
                    <Text variant="base-regular" color="primary" role="cell">
                      {row.label}
                    </Text>
                    <span role="cell">{row.render('from')}</span>
                    <span role="cell">{row.render('to')}</span>
                  </div>
                ))
              )}
            </div>

            {/* A failed diff request must not read as "no changes" — surface
                it explicitly (the params table above still renders, it comes
                from separate model-details requests). */}
            {diffError ? (
              <AlertBanner
                feedback="error"
                text="Couldn't load the record changes for these versions. Close the modal and try again."
                showButton={false}
                showCloseButton={false}
              />
            ) : (
              <>
                {/* Generative tasks have no labels/entities — the distribution
                    section would only ever say "No label data available". */}
                {outputLabel ? null : (
                  <LabelDistributionSection
                    distribution={diff?.distribution}
                    fromLabel={fromVersionLabel}
                    toLabel={toVersionLabel}
                    loading={diffLoading && !diff}
                  />
                )}

                <ChangedRecordsSection
                  records={diff?.records || []}
                  total={diff?.total || 0}
                  page={page}
                  pageSize={pageSize}
                  loading={diffLoading}
                  annotationsKind={diff?.distribution?.kind || 'labels'}
                  outputLabel={outputLabel}
                  singleLabel={singleLabel}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  onViewRecord={setViewedRecord}
                  allLabels={allLabels}
                />

                {/* Final table row per Figma (node 5211:36924): "Train Sets" label in
                    the params column, one download card under each version column. */}
                <div className="vdm-table vdm-params-table vdm-trainsets-table" role="table">
                  <div className="vdm-table-row" role="row">
                    <Text variant="base-regular" color="primary" role="cell">
                      Train Sets
                    </Text>
                    <span role="cell">
                      <TrainSetCard
                        summary={diff?.train_sets?.from}
                        eventId={fromEventId}
                        clientId={clientId}
                        usecaseId={usecaseId}
                        taskType={currentUsecase?.task_type}
                        versionLabel={fromVersionLabel}
                        taskName={taskName}
                      />
                    </span>
                    <span role="cell">
                      <TrainSetCard
                        summary={diff?.train_sets?.to}
                        eventId={toEventId}
                        clientId={clientId}
                        usecaseId={usecaseId}
                        taskType={currentUsecase?.task_type}
                        versionLabel={toVersionLabel}
                        taskName={taskName}
                      />
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal>
      </SparkModalPortal>
      {viewedRecord ? (
        <RecordDiffModal
          record={viewedRecord}
          fromLabel={fromVersionLabel}
          toLabel={toVersionLabel}
          outputLabel={outputLabel}
          singleLabel={singleLabel}
          allLabels={allLabels}
          onClose={() => setViewedRecord(null)}
        />
      ) : null}
    </>
  );
};

export default VersionsDiffModal;
